import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { ChevronDown, ChevronLeft, ChevronRight } from "lucide-react";
import { Button } from "./ui/button";
import { useData } from "../contexts/DataContext";
import { motion, AnimatePresence } from "framer-motion";

const DEFAULT_HERO_VIDEO =
  "https://ik.imagekit.io/hqhiltiie/Nyinimu/Sequence%2001.mp4?updatedAt=1763030398296";
const DEFAULT_POSTER =
  "https://images.unsplash.com/photo-1502005229762-cf1b2da7c52f?q=80&w=1400&auto=format&fit=crop";
const VIDEO_SLIDES = [
  {
    title: "Luxury Living In Motion",
    subtitle: "Glide through every room before you ever step inside—crafted for modern buyers.",
    video_url: "https://ik.imagekit.io/hqhiltiie/Nyinimu/slide%204.mp4?updatedAt=1763047340608",
    ctaLink: "/contact",
    ctaLabel: "Book Now",
  },
  {
    title: "Every Detail, Every Day",
    subtitle: "Flexible daily payments meet premium finishes for families on the move.",
    video_url: "https://ik.imagekit.io/hqhiltiie/Nyinimu/slide%202.mp4?updatedAt=1763047016794",
    ctaLink: "/contact",
    ctaLabel: "Book Now",
  },
  {
    title: "Designed For Your Story",
    subtitle: "Smart layouts, resort amenities, and seamless ownership with Nyinimu.",
    video_url: "https://ik.imagekit.io/hqhiltiie/Nyinimu/slide%203.mp4?updatedAt=1763046974203",
    ctaLink: "/contact",
    ctaLabel: "Book Now",
  },
  {
    title: "Your Home Journey Starts Here",
    subtitle: "Experience the future of homeownership with daily payments that fit your life.",
    video_url: "https://ik.imagekit.io/hqhiltiie/Nyinimu/Sequence%2001.mp4?updatedAt=1763030398296",
    ctaLink: "/contact",
    ctaLabel: "Start Today",
  },
];

const HeroSection = () => {
  const { heroSlides, loading } = useData();
  const [currentSlide, setCurrentSlide] = useState(0);
  
  // Filter active slides and sort by order
  const fetchedSlides = heroSlides
    .filter(slide => slide.active !== false) // Backend uses 'active' not 'is_active'
    .sort((a, b) => (a.order || 0) - (b.order || 0));
  const slides = [...fetchedSlides, ...VIDEO_SLIDES];

  // Auto-advance slides
  useEffect(() => {
    if (slides.length > 0) {
      const timer = setInterval(() => {
        setCurrentSlide((prev) => (prev + 1) % slides.length);
      }, 5000);
      return () => clearInterval(timer);
    }
  }, [slides.length]);

  // Reset current slide if out of bounds when slides change
  useEffect(() => {
    if (currentSlide >= slides.length && slides.length > 0) {
      setCurrentSlide(0);
    }
  }, [slides.length, currentSlide]);

  const nextSlide = () => {
    if (slides.length > 0) {
      setCurrentSlide((prev) => (prev + 1) % slides.length);
    }
  };

  const prevSlide = () => {
    if (slides.length > 0) {
      setCurrentSlide((prev) => (prev - 1 + slides.length) % slides.length);
    }
  };

  const goToSlide = (index) => {
    if (index >= 0 && index < slides.length) {
      setCurrentSlide(index);
    }
  };

  const currentSlideData = slides.length > 0 ? slides[currentSlide] : null;

  // Show loading state
  if (loading && slides.length === 0) {
    return (
      <section className="relative h-screen overflow-hidden bg-stone-900 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-16 w-16 border-b-2 border-[#81be00] mx-auto mb-4"></div>
          <p className="text-white">Loading...</p>
        </div>
      </section>
    );
  }

  // Show default slide if no slides available
  if (!slides || slides.length === 0) {
    return (
      <section className="relative h-screen overflow-hidden bg-stone-900 flex items-center justify-center">
        <div className="text-white text-center z-10 px-4">
          <h1 className="text-5xl md:text-7xl font-thin tracking-[0.25em] mb-8 title-font">
            NYINIMU PROPERTIES
          </h1>
          <p className="text-lg font-light tracking-widest opacity-95 uppercase">
            DAILY PAYMENTS. YOUR HOME. BUY DAILY. OWN FOREVER.
          </p>
        </div>
      </section>
    );
  }

  if (!currentSlideData) {
    return (
      <section className="relative h-screen overflow-hidden bg-stone-900 flex items-center justify-center">
        <div className="text-white text-center z-10 px-4">
          <h1 className="text-5xl md:text-7xl font-thin tracking-[0.25em] mb-8 title-font">
            NYINIMU PROPERTIES
          </h1>
          <p className="text-lg font-light tracking-widest opacity-95 uppercase">
            DAILY PAYMENTS. YOUR HOME. BUY DAILY. OWN FOREVER.
          </p>
        </div>
      </section>
    );
  }

  const heroVideoSource =
    (currentSlideData.video_url && currentSlideData.video_url.trim()) ||
    DEFAULT_HERO_VIDEO;
  const backgroundPoster = currentSlideData.background_image || undefined;
  const interiorPoster = currentSlideData.interior_image || undefined;

  return (
    <section
      className="relative h-screen overflow-hidden"
    >
      {/* Carousel Container */}
      <div className="relative w-full h-full">
        {/* Background Image */}
        <div className="absolute inset-0 w-full h-full">
          <AnimatePresence mode="wait">
            <motion.video
              key={`bg-${currentSlide}`}
              src={heroVideoSource}
              className="w-full h-full object-cover"
              autoPlay
              muted
              loop
              playsInline
              poster={backgroundPoster || DEFAULT_POSTER}
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 1 }}
            />
          </AnimatePresence>
          <div className="absolute inset-0 bg-gradient-to-b from-black/50 via-black/50 to-black/50"></div>
        </div>



        {/* Hero Content (left-bottom) */}
        <div className="absolute left-4 sm:left-8 md:left-12 lg:left-16 bottom-6 sm:bottom-10 z-10 text-left max-w-full sm:max-w-5xl px-4 sm:px-0">
          <AnimatePresence mode="wait">
            <motion.div
              key={`text-${currentSlide}`}
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -30 }}
              transition={{ duration: 0.8 }}
              className="backdrop-blur-sm bg-black/10 rounded-2xl p-6 sm:p-8 border border-white/10 shadow-[0_8px_32px_rgba(0,0,0,0.3)]"
            >
              <h1 className="text-xl sm:text-2xl md:text-4xl lg:text-6xl xl:text-7xl 2xl:text-8xl font-semibold tracking-[0.25em] mb-3 sm:mb-6 leading-tight text-[#81be00] title-font drop-shadow-lg">
                {currentSlideData.title}
              </h1>
              <p className="text-sm sm:text-base md:text-lg lg:text-xl text-white/90 font-light max-w-xs sm:max-w-md drop-shadow-md">
                {currentSlideData.subtitle}
              </p>
              <div className="mt-6">
                <Link to={currentSlideData.ctaLink || "/contact"}>
                  <Button className="bg-[#81be00] hover:bg-[#6a9e00] text-stone-900 px-6 py-3 rounded-lg shadow-[0_0_20px_rgba(129,190,0,0.3)] hover:shadow-[0_0_30px_rgba(129,190,0,0.5)] transition-all duration-300 font-semibold">
                    {currentSlideData.ctaLabel || "Book Now"}
                  </Button>
                </Link>
              </div>
            </motion.div>
          </AnimatePresence>
        </div>

        {/* Carousel Navigation Arrows */}
        <div className="absolute left-2 sm:left-8 top-1/2 transform -translate-y-1/2 z-20">
          <div className="flex flex-col space-y-3 sm:space-y-6">
            <Button
              aria-label="Previous Slide"
              variant="ghost"
              size="sm"
              onClick={prevSlide}
              className="w-12 h-12 sm:w-14 sm:h-14 rounded-full bg-black/20 backdrop-blur-md hover:bg-black/40 text-white border border-white/20 hover:border-white/40 shadow-[0_0_20px_rgba(255,255,255,0.1)] hover:shadow-[0_0_30px_rgba(255,255,255,0.2)] transition-all duration-300 group"
            >
              <ChevronLeft size={18} className="sm:size-6 group-hover:scale-110 transition-transform" />
            </Button>

            <Button
              aria-label="Next Slide"
              variant="ghost"
              size="sm"
              onClick={nextSlide}
              className="w-12 h-12 sm:w-14 sm:h-14 rounded-full bg-black/20 backdrop-blur-md hover:bg-black/40 text-white border border-white/20 hover:border-white/40 shadow-[0_0_20px_rgba(255,255,255,0.1)] hover:shadow-[0_0_30px_rgba(255,255,255,0.2)] transition-all duration-300 group"
            >
              <ChevronRight size={18} className="sm:size-6 group-hover:scale-110 transition-transform" />
            </Button>
          </div>
        </div>

        {/* Slide Indicators */}
        <div className="absolute bottom-24 sm:bottom-32 left-2 sm:left-8 z-20">
          <div className="flex flex-col space-y-3 sm:space-y-4">
            {slides.map((_, index) => (
              <button
                key={index}
                onClick={() => goToSlide(index)}
                aria-label={`Go to slide ${index + 1}`}
                className={`relative w-3 h-3 sm:w-4 sm:h-4 rounded-full transition-all duration-500 ${
                  index === currentSlide
                    ? "bg-[#81be00] shadow-[0_0_20px_rgba(129,190,0,0.8)] scale-125"
                    : "bg-white/30 hover:bg-white/60 hover:shadow-[0_0_10px_rgba(255,255,255,0.5)]"
                }`}
              >
                {index === currentSlide && (
                  <div className="absolute inset-0 rounded-full bg-[#81be00] animate-ping opacity-30"></div>
                )}
              </button>
            ))}
          </div>
        </div>

        {/* Scroll Down Indicator */}
        <div className="absolute bottom-4 sm:bottom-8 left-1/2 transform -translate-x-1/2 sm:left-1/3">
          <div className="flex flex-col items-center text-white">
            <div className="w-8 h-8 sm:w-10 sm:h-10 border border-white flex items-center justify-center mb-1 sm:mb-2 hover:bg-white hover:text-black transition-all duration-300 cursor-pointer">
              <ChevronDown size={16} className="sm:size-5" />
            </div>
          </div>
        </div>

        {/* Slide Progress Bar */}
        <div className="absolute bottom-0 left-0 right-0 z-20">
          <div className="w-full h-2 bg-black/20 backdrop-blur-sm">
            <motion.div
              key={`progress-${currentSlide}`}
              className="h-full bg-gradient-to-r from-[#81be00] to-[#a6d94a] shadow-[0_0_10px_rgba(129,190,0,0.5)]"
              initial={{ width: "0%" }}
              animate={{ width: "100%" }}
              transition={{ duration: 5, ease: "linear" }}
            />
          </div>
        </div>

        {/* Decorative Slide Counter */}
        <div className="absolute bottom-12 sm:bottom-16 right-4 sm:right-16 hidden sm:block z-10">
          <div className="text-white text-right">
            <div className="text-xs sm:text-sm opacity-70 mb-1">
              {String(currentSlide + 1).padStart(2, "0")} /{" "}
              {String(slides.length).padStart(2, "0")}
            </div>
            <div className="w-px h-8 sm:h-12 bg-white opacity-60 ml-auto"></div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default HeroSection;
